import React from "react";
import { motion } from "framer-motion";
import {
  FaUniversity,
  FaBookOpen,
  FaUsers,
  FaLaptopCode,
  FaGraduationCap,
} from "react-icons/fa";

const streams = [
  {
    title: "Medical",
    icon: <FaGraduationCap />,
    subjects: [
      "English (Core)",
      "Biology",
      "Physics",
      "Chemistry",
      "Physical Education / Painting / Music",
    ],
  },
  {
    title: "Non-Medical",
    icon: <FaLaptopCode />,
    subjects: [
      "English (Core)",
      "Mathematics",
      "Physics",
      "Chemistry",
      "Computer Science / Physical Education",
    ],
  },
  {
    title: "Commerce",
    icon: <FaBookOpen />,
    subjects: [
      "English (Core)",
      "Accountancy",
      "Business Studies",
      "Economics",
      "Mathematics / Physical Education",
    ],
  },
  {
    title: "Humanities",
    icon: <FaUsers />,
    subjects: [
      "English (Core)",
      "History",
      "Political Science",
      "Psychology",
      "Economics",
    ],
  },
];

const HighSchool = () => {
  return (
    <section className="relative w-full py-32 overflow-hidden bg-gradient-to-br from-[#f8fbff] via-[#f5f7fc] to-[#eef4ff]">

      <div className="absolute top-[-120px] left-[-120px] w-[420px] h-[420px] bg-[#d4af37]/15 blur-3xl rounded-full"></div>

      <div className="absolute bottom-[-120px] right-[-120px] w-[420px] h-[420px] bg-[#07294d]/10 blur-3xl rounded-full"></div>

      <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#07294d_1px,transparent_1px),linear-gradient(to_bottom,#07294d_1px,transparent_1px)] bg-[size:70px_70px]"></div>

      <div className="max-w-[1550px] mx-auto px-6 relative z-10">

        {/* HERO */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-28"
        >

          <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white border border-[#d4af37]/40 shadow-lg mb-10">

            <span className="text-[#d4af37] text-xl">🏫</span>

            <p className="uppercase tracking-[6px] text-[#d4af37] text-sm font-black">
              High School Programme
            </p>

          </div>

          <h1 className="text-5xl md:text-7xl xl:text-8xl font-black text-[#07294d] leading-[1.08]">

            <span className="relative inline-block pb-8">

              High
              <br />
              School

              <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-[105%] h-6 bg-[#d4af37]/20 rounded-full"></span>

              <span className="absolute left-1/2 -translate-x-1/2 bottom-2 w-[70%] h-[4px] bg-white rounded-full"></span>

            </span>

          </h1>

          <p className="max-w-[1100px] mx-auto text-gray-600 text-xl leading-10 mt-14">

            Our pedagogy combines established learning theories with
            innovative teaching practices to provide students with an
            engaging, collaborative and future-ready educational experience.

          </p>

        </motion.div>

        {/* Teaching Methodology */}

        <div className="max-w-5xl mx-auto mb-32">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-white rounded-[50px] shadow-[0_30px_100px_rgba(0,0,0,0.08)] p-10 md:p-14 relative overflow-hidden">

              <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

              <div className="w-24 h-24 rounded-[32px] bg-[#07294d] text-white flex items-center justify-center text-5xl shadow-2xl mb-10">

                <FaUniversity />

              </div>

              <h2 className="text-5xl font-black text-[#07294d] leading-tight mb-8">

                Our Teaching
                <br />
                Methodology

              </h2>

              <div className="w-28 h-[6px] bg-[#d4af37] rounded-full mb-10"></div>

              <p className="text-gray-600 text-xl leading-10 mb-8">

                Our pedagogy focuses on how teaching and learning occur.
                We use an array of teaching methodologies including direct
                instruction, collaborative learning and inquiry-based learning.

              </p>

              <p className="text-gray-600 text-xl leading-10 mb-8">

                We blend consistency with innovation by combining
                established learning theories and modern educational
                practices that inspire curiosity and active participation.

              </p>

              <p className="text-gray-600 text-xl leading-10">

                Every learning experience is designed to help students
                improve academic performance, develop critical thinking,
                strengthen communication skills and achieve their
                highest potential.

              </p>

            </div>

          </motion.div>

        </div>
                {/* ================= STREAMS ================= */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <h2 className="text-5xl md:text-6xl font-black text-[#07294d] leading-tight">

            <span className="relative inline-block pb-8">

              Streams Offered

              <span className="absolute left-1/2 -translate-x-1/2 bottom-1 w-[105%] h-5 bg-[#d4af37]/20 rounded-full"></span>

            </span>

          </h2>

          <p className="max-w-4xl mx-auto text-gray-600 text-xl leading-9 mt-6">

            Students can choose streams according to their interests,
            strengths and future career aspirations. Our curriculum
            prepares every learner for higher education and professional success.

          </p>

        </motion.div>

        {/* STREAM GRID */}

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-10 mb-32">

          {streams.map((stream, index) => (

            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="group relative bg-white rounded-[40px] overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.08)]"
            >

              <div className="absolute top-0 left-0 w-full h-[7px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

              <h1 className="absolute top-4 right-5 text-[80px] font-black text-[#07294d]/5">

                0{index + 1}

              </h1>

              <div className="p-8 relative z-10">

                <div className="w-20 h-20 rounded-[28px] bg-[#07294d] text-white flex items-center justify-center text-4xl shadow-2xl mb-8 group-hover:bg-[#d4af37] group-hover:text-[#07294d] transition-all duration-500">

                  {stream.icon}

                </div>

                <h3 className="text-3xl font-black text-[#07294d] mb-6">

                  {stream.title}

                </h3>

                <div className="w-20 h-[5px] bg-[#d4af37] rounded-full mb-8"></div>

                <ul className="space-y-4">

                  {stream.subjects.map((subject, i) => (

                    <li
                      key={i}
                      className="flex items-start gap-3 text-gray-600 leading-7"
                    >

                      <span className="mt-2 w-2.5 h-2.5 rounded-full bg-[#d4af37]"></span>

                      <span>{subject}</span>

                    </li>

                  ))}

                </ul>

              </div>

            </motion.div>

          ))}

        </div>
                {/* ================= ACADEMIC PATHWAYS ================= */}

        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-[45px] shadow-[0_30px_80px_rgba(0,0,0,0.08)] p-12 mb-32 relative overflow-hidden"
        >

          <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

          <h2 className="text-4xl font-black text-[#07294d] mb-8">
            Academic Pathways
          </h2>

          <div className="w-24 h-[6px] bg-[#d4af37] rounded-full mb-10"></div>

          <p className="text-gray-600 text-xl leading-10">

            The school offers four major streams for Classes XI & XII:
            <strong> Medical, Non-Medical, Commerce and Humanities.</strong>
            Students are encouraged to select the stream that best matches
            their aptitude, interests and long-term career goals.
            Through expert faculty, practical exposure and career guidance,
            we ensure every learner receives the support needed to excel.

          </p>

        </motion.div>

        {/* ================= EXAMINATION POLICY ================= */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <h2 className="text-5xl md:text-6xl font-black text-[#07294d]">
            Examination Policy
          </h2>

          <p className="max-w-4xl mx-auto text-gray-600 text-xl leading-9 mt-6">

            Our assessment system follows a transparent, fair and
            comprehensive evaluation process that encourages continuous
            learning and overall student development.

          </p>

        </motion.div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-10">

          {[
            {
              title: "Continuous Evaluation",
              desc: "Students are assessed continuously through a comprehensive evaluation system focusing on overall development.",
              icon: "📊",
            },
            {
              title: "Semester System",
              desc: "The academic session is divided into two semesters: April–September and October–March.",
              icon: "📅",
            },
            {
              title: "Mandatory Attendance",
              desc: "Students are expected to appear in all tests and examinations unless excused on valid medical grounds.",
              icon: "📝",
            },
            {
              title: "Fair Examination",
              desc: "Any student found using unfair means during examinations is liable for disciplinary action.",
              icon: "⚖️",
            },
            {
              title: "Final Assessment",
              desc: "Final results are based on regular tests, assignments, classroom performance and overall consistency.",
              icon: "🏆",
            },
            {
              title: "No Re-Examination",
              desc: "Re-examinations are not conducted. Students are encouraged to maintain regular academic performance.",
              icon: "🎯",
            },
          ].map((item, index) => (

            <motion.div
              key={index}
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-[40px] shadow-[0_25px_80px_rgba(0,0,0,0.08)] overflow-hidden"
            >

              <div className="h-[7px] bg-gradient-to-r from-[#07294d] via-[#d4af37] to-[#07294d]"></div>

              <div className="p-10">

                <div className="w-20 h-20 rounded-[28px] bg-[#07294d] text-white flex items-center justify-center text-4xl mb-8">

                  {item.icon}

                </div>

                <h3 className="text-3xl font-black text-[#07294d] mb-5">

                  {item.title}

                </h3>

                <div className="w-20 h-[5px] bg-[#d4af37] rounded-full mb-7"></div>

                <p className="text-gray-600 text-lg leading-9">

                  {item.desc}

                </p>

              </div>

            </motion.div>

          ))}

        </div>

      </div>

    </section>
  );
};

export default HighSchool;